import * as t from 'io-ts'
import { DateFromISOString } from 'io-ts-types/lib/DateFromISOString'
import { contramap, getSemigroup, ordNumber, ordString } from 'fp-ts/lib/Ord'
import { fold } from 'fp-ts/lib/Semigroup'
import { Future } from 'funfix'
import * as O from 'fp-ts/lib/Option'
import { NumberFromString, ofType, unionize, UnionOf } from 'src/framework'
import { Ord } from 'fp-ts/lib/Ord'
import { PaymentBody, PaymentResponse } from '@yobora/models/api/payment'
import { QuestionId, TestResolver } from '@yobora/models/models'
import { Status } from '@yobora/models/rights'
import { TestStatus } from '@yobora/models/testSessions'
import { pipe } from 'fp-ts/lib/pipeable'
import * as E from 'fp-ts/lib/Either'

const decodeResponse = <A>(runType: t.Decoder<unknown, A>) => (value: unknown): O.Option<A> =>
  pipe(
    runType.decode(value),
    E.fold(_err => O.none, O.some)
  )

const safely = <T>(x: Future<T>) => <U>(runType: t.Decoder<unknown, U>) =>
  x.attempt().map(res => res.fold(_err => O.none, decodeResponse(runType)))

const Post = <T>(url: string, value: T, runType: t.Type<T, unknown>): Future<unknown> => {
  const keys = Object.keys(value)
  keys.forEach((prop: any) => {
    if ((value as any)[prop] === null) {
      delete (value as any)[prop]
    }
  })
  return Future.fromPromise(
    fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(runType.encode(value))
    }).then(resp => resp.json())
  )
}

const Put = <T>(url: string, value: T, runType: t.Type<T, unknown>): Future<unknown> => {
  const keys = Object.keys(value)
  keys.forEach((prop: any) => {
    if ((value as any)[prop] === null) {
      delete (value as any)[prop]
    }
  })
  return Future.fromPromise(
    fetch(url, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(runType.encode(value))
    }).then(resp => resp.json())
  )
}

const Get: (url: string) => Future<unknown> = url => Future.fromPromise(fetch(url).then(resp => resp.json()))

// ---

export interface ResultsOf<T> {
  result: T[]
}
const ResultsOf = <T>(o: t.Type<T, unknown>): t.Type<ResultsOf<T>, unknown> =>
  t.interface({
    result: t.array(o)
  })

export interface ResultOf<T> {
  result: T
}
const ResultOf = <T>(o: t.Type<T, unknown>): t.Type<ResultOf<T>, unknown> =>
  t.interface({
    result: o
  })

export interface ResultOfString extends ResultOf<string> {}
const ResultOfString = ResultOf(t.string)

// ---

export interface Right {
  id: number
  name: string
  description: string
}

export const ordRight: Ord<Right> = contramap((r: Right) => r.id)(ordNumber)

export const Right: t.Type<Right> = t.interface({
  id: t.number,
  name: t.string,
  description: t.string
})

export interface GetRightsResult {
  result: Right[]
}
export const GetRightsResult: t.Type<GetRightsResult> = t.interface({
  result: t.array(Right)
})

export interface CreateCompanyPayload {
  creatorToken: string
  name: string
  address: string
  city: string
  country: string
  postalCode: string
  phoneNumber: string
  groupingCode: string | null
}
export const CreateCompanyPayload: t.Type<CreateCompanyPayload> = t.interface({
  creatorToken: t.string,
  name: t.string,
  address: t.string,
  city: t.string,
  country: t.string,
  postalCode: t.string,
  phoneNumber: t.string,
  groupingCode: t.union([t.string, t.null])
})

export interface CompanyContent {
  id: string
  name: string
  address: string
  city: string
  country: string
  postalCode: string
  phoneNumber: string
  groupingCode: string | null
}

export type Price = number
export const Price = t.union([t.number, NumberFromString])
export interface TestContent {
  id: string
  name: string
  displayName: string
  inviteSubject: string
  inviteEmail: string
  price: Price
}
export const TestContent: t.Type<TestContent, unknown> = t.interface({
  id: t.string,
  name: t.string,
  displayName: t.string,
  inviteSubject: t.string,
  inviteEmail: t.string,
  price: Price
})
const TestContentById: Ord<TestContent> = pipe(
  ordString,
  contramap(x => x.id!)
)
const TestContentByName: Ord<TestContent | CreateTestModel> = pipe(
  ordString,
  contramap(x => x.name)
)
const TestContentByDisplayName: Ord<TestContent | CreateTestModel> = pipe(
  ordString,
  contramap(x => x.displayName)
)
const TestContentByInviteSubject: Ord<TestContent | CreateTestModel> = pipe(
  ordString,
  contramap(x => x.inviteSubject)
)
const TestContentByInviteEmail: Ord<TestContent | CreateTestModel> = pipe(
  ordString,
  contramap(x => x.inviteEmail)
)
const TestContentByPrice: Ord<TestContent | CreateTestModel> = pipe(
  ordNumber,
  contramap(x => x.price)
)

export const OrdTestContent: Ord<TestContent> = fold(getSemigroup<TestContent>())(TestContentById, [
  TestContentByName,
  TestContentByDisplayName,
  TestContentByInviteSubject,
  TestContentByInviteEmail,
  TestContentByPrice
])

export const OrdCreateTestContent: Ord<CreateTestModel> = fold(getSemigroup<CreateTestModel>())(TestContentByName, [
  TestContentByDisplayName,
  TestContentByInviteSubject,
  TestContentByInviteEmail,
  TestContentByPrice
])

export interface GetTestsResult {
  result: TestContent[]
}

export const GetTestsResult: t.Type<GetTestsResult, unknown> = t.interface({
  result: t.array(TestContent)
})

export interface GetTestsNamesResult {
  result: string[]
}

export const GetTestsNamesResult: t.Type<GetTestsNamesResult, unknown> = t.interface({
  result: t.array(t.string)
})

export interface GetCompanyResult {
  result: {
    id: string
    name: string
    address: string
    city: string
    country: string
    postalCode: string
    phoneNumber: string
    groupingCode: string | null
  }
}
export const GetCompanyResult: t.Type<GetCompanyResult> = t.interface({
  result: t.interface({
    id: t.string,
    name: t.string,
    address: t.string,
    city: t.string,
    country: t.string,
    postalCode: t.string,
    phoneNumber: t.string,
    groupingCode: t.union([t.string, t.null])
  })
})

export interface GetCompaniesResult {
  result: {
    id: string
    name: string
    address: string
    city: string
    country: string
    postalCode: string
    phoneNumber: string
    groupingCode: string | null
  }[]
}
export const GetCompaniesResult: t.Type<GetCompaniesResult> = t.interface({
  result: t.array(
    t.interface({
      id: t.string,
      name: t.string,
      address: t.string,
      city: t.string,
      country: t.string,
      postalCode: t.string,
      phoneNumber: t.string,
      groupingCode: t.union([t.string, t.null])
    })
  )
})

export interface Authenticated {
  type: 'authenticated'
  companyId: string
  token: string
  rights: Status[]
  initials: string
}
export interface NotAuthenticated {
  type: 'notAuthenticated'
}
export const authenticationStatus = unionize({
  ...ofType<Authenticated>('authenticated'),
  ...ofType<NotAuthenticated>('notAuthenticated')
})
export type AuthenticationStatus = UnionOf<typeof authenticationStatus>

// TokenStatus

export interface ValidToken {
  type: 'validToken'
}
const validToken = ofType<ValidToken>('validToken')

export interface InvalidToken {
  type: 'invalidToken'
}
const invalidToken = ofType<InvalidToken>('invalidToken')

export interface NoToken {
  type: 'noToken'
}
const noToken = ofType<NoToken>('noToken')

export const tokenStatus = unionize({
  ...validToken,
  ...invalidToken,
  ...noToken
})
export type TokenStatus = UnionOf<typeof tokenStatus>

//

export interface AuthenticateRequest {
  email: string
  password: string
}
export const AuthenticateRequest = t.interface({
  email: t.string,
  password: t.string
})

export interface AuthenticateResponse {
  initials: string
  token: string
  companyId: string
  rights: Status[]
}
const AuthenticateResponse: t.Type<AuthenticateResponse, unknown> = t.interface({
  initials: t.string,
  token: t.string,
  companyId: t.string,
  rights: t.array(Status.Type)
})

const isValidAuthentication = (resp: AuthenticateResponse) => resp.token.length !== 0

const authenticationForResponse = (respOpt: O.Option<AuthenticateResponse>) =>
  pipe(
    respOpt,
    O.filter(isValidAuthentication),
    O.fold(authenticationStatus.notAuthenticated, authenticationStatus.authenticated)
  )

export interface InviteUserToTest {
  email: string
  firstName: string
  lastName: string
  testId: string
  dueDate: string
  token: string
}
const InviteUserToTest: t.Type<InviteUserToTest> = t.interface({
  email: t.string,
  firstName: t.string,
  lastName: t.string,
  testId: t.string,
  dueDate: t.string,
  token: t.string
})

export interface InviteSubUser {
  email: string
  sectionName: string
}

const InviteSubUser: t.Type<InviteSubUser> = t.interface({
  email: t.string,
  sectionName: t.string
})

export interface InviteUsersToSubTest {
  id: string
  invites: InviteSubUser[]
}
const InviteUsersToSubTest: t.Type<InviteUsersToSubTest> = t.interface({
  id: t.string,
  invites: t.array(InviteSubUser)
})

export type InviteUserToTestResponse = ResultOfString
const InviteUserToTestResponse = ResultOfString

export type InviteUsersToSubTestResponse = ResultOfString
const InviteUsersToSubTestResponse = ResultOfString

export interface InviteUser {
  creatorToken: string
  email: string
  firstName: string
  lastName: string
  rightIds: number[]
  companyId: string
}
const InviteUser: t.Type<InviteUser> = t.interface({
  creatorToken: t.string,
  email: t.string,
  firstName: t.string,
  lastName: t.string,
  rightIds: t.array(t.number),
  companyId: t.string
})

export type InviteUserResponse = ResultOfString
const InviteUserResponse = ResultOfString

export interface Answer {
  id: QuestionId
  result: string
}
export const Answer: t.Type<Answer, unknown> = t.interface({
  id: QuestionId.Type,
  result: t.string
})

export interface SendTestResults {
  token: string
  answers: Answer[]
}
export const SendTestResults: t.Type<SendTestResults, unknown> = t.strict({
  token: t.string,
  answers: t.array(Answer)
})

export type SendTestResultsResponse = ResultOfString
const SendTestResultsResponse: t.Type<SendTestResultsResponse, unknown> = ResultOfString

//

export interface GetTestResults {
  token: string
}
export const GetTestResults: t.Type<GetTestResults> = t.interface({
  token: t.string
})

export type GetTestResultsResponse = ResultOf<{ answersIds: string[] }>
const GetTestResultsResponse: t.Type<GetTestResultsResponse, unknown> = ResultOf(
  t.interface({ answersIds: t.array(t.string) })
)

//

export interface GetTest {
  id: string
  language: TestResolver.AvailableLanguages
}

const GetTest: t.Type<GetTest> = t.interface({
  id: t.string,
  language: t.keyof({
    fr: null,
    en: null,
    cn: null
  })
})
export type GetTestResponse = ResultOfString
const GetTestResponse = ResultOfString

export const PublicQuestion: t.Type<TestResolver.PublicQuestion, unknown> = t.interface({
  id: QuestionId.Type,
  text: t.string,
  answers: t.array(t.string),
  yesNoButtons: t.boolean
})

export const PublicInvitationSection: t.Type<TestResolver.PublicInvitationSection, unknown> = t.interface({
  numberOfInvites: t.number,
  sectionText: t.string,
  sectionName: t.string
})

export const PublicInvitation: t.Type<TestResolver.PublicInvitation, unknown> = t.interface({
  presentationText: t.string,
  sections: t.array(PublicInvitationSection)
})

export const PublicTestSession: t.Type<TestResolver.PublicTestSession, unknown> = t.interface({
  testFinished: t.boolean,
  firstName: t.union([t.string, t.undefined]),
  lastName: t.union([t.string, t.undefined]),
  email: t.string,
  invitationsSent: t.boolean,
  invitations: t.union([PublicInvitation, t.undefined]),
  test: t.array(PublicQuestion)
})

export interface TestDesc {
  id: string
  displayName: string
}
export const TestDesc = t.interface({
  id: t.string,
  displayName: t.string
})

export interface SubTestProgressionInformation {
  done: number
  total: number
}

const SubTestProgressionInformation: t.Type<SubTestProgressionInformation, unknown> = t.interface({
  done: t.number,
  total: t.number
})

export interface SubTestProgressionInformations {
  [group: string]: SubTestProgressionInformation
}

const SubTestProgressionInformations: t.Type<SubTestProgressionInformations, unknown> = t.record(
  t.string,
  SubTestProgressionInformation
)

export interface TestMissingReport {
  firstName: string
  lastName: string
  email: string
  isDone: boolean
  answersIds: string[]
  testName: string
  subTestSessions: SubTestSession[]
}

export interface SubTestSession {
  isDone: boolean
  group: string
  answersIds: string[]
}

export interface TestProgressionInformations {
  firstName: string
  lastName: string
  email: string
  testName: string
  testStatus: TestStatus
  dateInvitation: Date
  lastUpdate: Date
  dueDate: Date
  subTests: SubTestProgressionInformations | undefined
}

export interface TestProgressionInformations {
  firstName: string
  lastName: string
  email: string
  testName: string
  testStatus: TestStatus
  dateInvitation: Date
  lastUpdate: Date
  dueDate: Date
  subTests: SubTestProgressionInformations | undefined
}

const TestProgressionInformations: t.Type<TestProgressionInformations, unknown> = t.interface({
  firstName: t.string,
  lastName: t.string,
  email: t.string,
  testName: t.string,
  testStatus: TestStatus.Type,
  dateInvitation: DateFromISOString,
  lastUpdate: DateFromISOString,
  dueDate: DateFromISOString,
  subTests: t.union([SubTestProgressionInformations, t.undefined])
})

export interface CompanyCredits {
  credits: number
  test: TestDesc
}

const CompanyCredits: t.Type<CompanyCredits> = t.interface({
  credits: t.number,
  test: TestDesc
})

export interface PasswordAndToken {
  password: string
  token: string
}

const PasswordAndToken: t.Type<PasswordAndToken> = t.interface({
  password: t.string,
  token: t.string
})

export interface UserMeProfile {
  firstName: string
  lastName: string
  email: string
}

const UserMeProfile: t.Type<UserMeProfile> = t.interface({
  firstName: t.string,
  lastName: t.string,
  email: t.string
})

export interface UpdateMyProfile {
  firstName: string
  lastName: string
  email: string
  token: string
}

const UpdateMyProfile: t.Type<UpdateMyProfile> = t.interface({
  firstName: t.string,
  lastName: t.string,
  email: t.string,
  token: t.string
})

export interface UpdateMyPassword {
  oldPassword: string
  newPassword: string
  token: string
}

const UpdateMyPassword: t.Type<UpdateMyPassword> = t.interface({
  oldPassword: t.string,
  newPassword: t.string,
  token: t.string
})

export interface UpdateCompanyRequest {
  id: string | null
  creatorToken: string
  name: string
  address: string
  postalCode: string
  city: string
  country: string
  phoneNumber: string
  groupingCode: string | null
}
const UpdateCompanyRequest: t.Type<UpdateCompanyRequest, unknown> = t.interface({
  id: t.union([t.string, t.null]),
  creatorToken: t.string,
  name: t.string,
  address: t.string,
  postalCode: t.string,
  city: t.string,
  country: t.string,
  phoneNumber: t.string,
  groupingCode: t.union([t.string, t.null])
})

const TokenOK = t.interface({
  result: t.keyof({
    OK: null
  })
})

export interface CreateTestModel {
  name: string
  displayName: string
  inviteSubject: string
  inviteEmail: string
  price: Price
}

export const CreateTestModel: t.Type<CreateTestModel, unknown> = t.interface({
  name: t.string,
  displayName: t.string,
  inviteSubject: t.string,
  inviteEmail: t.string,
  price: Price
})

export interface CreditSummary {
  testName: string
  testId: string
  credits: number
}

export const CreditSummary: t.Type<CreditSummary, unknown> = t.interface({
  testName: t.string,
  testId: t.string,
  credits: t.number
})

export interface CreditsSummary {
  hasHabilitation: boolean
  credits: CreditSummary[]
}

export const CreditsSummary: t.Type<CreditsSummary, unknown> = t.interface({
  hasHabilitation: t.boolean,
  credits: t.array(CreditSummary)
})

export interface GiftCreditsRequest {
  token: string
  testId: string
  companyId: string
  credits: number
}

export const GiftCreditsRequest: t.Type<GiftCreditsRequest, unknown> = t.interface({
  token: t.string,
  testId: t.string,
  companyId: t.string,
  credits: t.number
})

export class DSService {
  //  private url = environment.url

  constructor(private readonly url: string) {}

  createCompany = (model: {
    creatorToken: string
    name: string
    address: string
    postalCode: string
    city: string
    country: string
    phoneNumber: string
    groupingCode: string
  }) => safely(Post(`${this.url}/v1/company`, model, CreateCompanyPayload))(t.string)

  getCompany = (token: string) => safely(Get(`${this.url}/v1/company?token=${token}`))(GetCompanyResult)

  getCompanies = () => safely(Get(`${this.url}/v1/companies`))(GetCompaniesResult)

  validateToken = (token: string) =>
    safely(Get(`${this.url}/v1/validateToken?token=${token}`))(TokenOK).map(res =>
      pipe(
        res,
        O.filter(x => x.result === 'OK'),
        O.fold(tokenStatus.noToken, _ok => tokenStatus.validToken())
      )
    )

  authenticate = (login: AuthenticateRequest) =>
    safely(Post(`${this.url}/v1/login`, login, AuthenticateRequest))(AuthenticateResponse).map(
      authenticationForResponse
    )

  getTests = (token: string) => safely(Get(`${this.url}/v1/tests?token=${token}`))(GetTestsResult)

  inviteUserToTest = (model: InviteUserToTest) =>
    safely(Post(`${this.url}/v1/testInvite`, model, InviteUserToTest))(InviteUserToTestResponse)

  inviteUsersToSubTest = (model: InviteUsersToSubTest) =>
    safely(Post(`${this.url}/v1/inviteSubTest`, model, InviteUsersToSubTest))(InviteUsersToSubTestResponse)

  inviteUser = (model: InviteUser) => safely(Post(`${this.url}/v1/inviteUser`, model, InviteUser))(InviteUserResponse)

  sendTestResult = (model: SendTestResults) => {
    const { token, answers } = model
    const data: SendTestResults = { token, answers }
    return safely(Post(`${this.url}/v1/testAnswers`, data, SendTestResults))(SendTestResultsResponse)
  }

  getTestResults = (model: GetTestResults) =>
    safely(Post(`${this.url}/v1/getTestAnswers`, model, GetTestResults))(GetTestResultsResponse)

  getTest = (model: GetTest) => safely(Post(`${this.url}/v1/getTest`, model, GetTest))(ResultOf(PublicTestSession))

  getTestInfos = (selectedTestName: string) =>
    safely(Get(`${this.url}/v1/getTestInfos?testName=${selectedTestName}`))(ResultOf(TestContent))

  setTestInfos = (_model: CreateTestModel, testId?: string) => {
    // FIXME: use general solution via encoder
    const { name, displayName, inviteSubject, inviteEmail, price } = _model
    const model: CreateTestModel = { name, displayName, inviteSubject, inviteEmail, price }
    return testId === undefined ? this.createTestInfos(model) : this.updateTestInfos(model, testId)
  }

  getTestsStatuses = (token: string) =>
    safely(Get(`${this.url}/v1/testsStatuses?token=${token}`))(ResultsOf(TestProgressionInformations))

  createTestInfos = (model: CreateTestModel): Future<O.Option<ResultOf<number>>> =>
    safely(Post(`${this.url}/v1/test`, model, CreateTestModel))(ResultOf(t.number))

  updateTestInfos = (model: CreateTestModel, testId: string): Future<O.Option<ResultOf<number>>> =>
    safely(Put(`${this.url}/v1/test?id=${testId!}`, model, CreateTestModel))(ResultOf(t.number))

  getTestsNames = (token: string) => safely(Get(`${this.url}/v1/testsNames?token=${token}`))(ResultsOf(t.string))

  getCompaniesCredits = (token: string) =>
    safely(Get(`${this.url}/v1/company/credits?token=${token}`))(ResultsOf(CompanyCredits))

  getRights = () => safely(Get(`${this.url}/v1/rights`))(GetRightsResult)

  getUsers = () => safely(Get(`${this.url}/v1/users`))(ResultsOf(t.unknown))

  finalizeAccountCreation = (model: PasswordAndToken) =>
    safely(Post(`${this.url}/v1/finalizeAccountCreation`, model, PasswordAndToken))(ResultOf(t.unknown))

  changePassword = (model: PasswordAndToken) =>
    safely(Put(`${this.url}/v1/changePassword`, model, PasswordAndToken))(ResultOf(t.unknown))

  me = (token: string) => safely(Get(`${this.url}/v1/me?token=${token}`))(ResultOf(UserMeProfile))

  updateMyProfile = (model: UpdateMyProfile) =>
    safely(Put(`${this.url}/v1/me`, model, UpdateMyProfile))(ResultOf(UserMeProfile))

  updateMyPassword = (model: UpdateMyPassword) =>
    safely(Put(`${this.url}/v1/me/password`, model, UpdateMyPassword))(t.unknown)

  updateCompany = (model: UpdateCompanyRequest) =>
    safely(Put(`${this.url}/v1/company`, model, UpdateCompanyRequest))(t.unknown)

  getCreditsSummary = (token: string, companyId: string) =>
    safely(Get(`${this.url}/v1/credits?token=${token}&companyId=${companyId}`))(ResultOf(CreditsSummary))

  giftCredits = (model: GiftCreditsRequest) =>
    safely(Post(`${this.url}/v1/credits/gift`, model, GiftCreditsRequest))(t.unknown)

  lostPassword = (email: string) =>
    Post(`${this.url}/v1/lostPassword`, { email }, t.interface({ email: t.string })).map(
      decodeResponse(ResultOf(t.unknown))
    )

  processPayment = (paymentBody: PaymentBody) =>
    Post(`${this.url}/v1/payment`, paymentBody, PaymentBody).map(decodeResponse(ResultOf(PaymentResponse)))
}



// WEBPACK FOOTER //
// ./src/services/ds.service.ts